import { useState } from "react";
import { motion } from "framer-motion";
import { Download, FileText, Eye, X, Search, Filter } from "lucide-react";
import { RegulationTypes, regulationsFiles } from "../lib/utils";

const Regulation = () => {
  const [selectedFile, setSelectedFile] = useState<RegulationTypes | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleDownload = (url: string) => {
    window.open(url, "_blank");
  };

  const handlePreview = (file: RegulationTypes) => {
    setSelectedFile(file);
    setIsPreviewOpen(true);
  };

  const filteredFiles = regulationsFiles.filter(file =>
    file.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1E3D1A]">
        <div className="absolute inset-0 bg-[url('/images/telecharger-fichiers.webp')] opacity-50" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Réglementations
            </h1>
            <p className="text-xl text-white/90">
              Consultez et téléchargez les documents officiels et réglementations
              du Projet Mobilier National.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Files Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto mb-8"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un document..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pl-12 rounded-2xl border border-gray-200 focus:border-[#1E3D1A] focus:ring-2 focus:ring-[#1E3D1A]/20 outline-none transition-all duration-300"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <button className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-xl bg-[#1E3D1A]/10 hover:bg-[#1E3D1A]/20 transition-colors">
                <Filter className="w-5 h-5 text-[#1E3D1A]" />
              </button>
            </div>
          </motion.div>

          {/* Files Grid */}
          <div className="grid grid-cols-1 gap-6 max-w-5xl mx-auto">
            {filteredFiles.map((file, index) => (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className="bg-gradient-to-br from-[#1E3D1A]/10 to-[#1E3D1A]/5 p-4 rounded-xl group-hover:from-[#1E3D1A]/20 group-hover:to-[#1E3D1A]/10 transition-all duration-300">
                    <FileText className="w-8 h-8 text-[#1E3D1A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-[#1E3D1A] mb-2 line-clamp-2 group-hover:text-[#1E3D1A]/90 transition-colors">
                      {file.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#1E3D1A]/20"></span>
                        {file.details}
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#1E3D1A]/20"></span>
                        {file.size} MB
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handlePreview(file)}
                      className="p-3 rounded-xl bg-[#1E3D1A]/10 hover:bg-[#1E3D1A]/20 transition-all duration-300 group-hover:scale-110"
                      title="Prévisualiser"
                    >
                      <Eye className="w-5 h-5 text-[#1E3D1A]" />
                    </button>
                    <button
                      onClick={() => handleDownload(file.url)}
                      className="p-3 rounded-xl bg-[#1E3D1A]/10 hover:bg-[#1E3D1A]/20 transition-all duration-300 group-hover:scale-110"
                      title="Télécharger"
                    >
                      <Download className="w-5 h-5 text-[#1E3D1A]" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PDF Preview Modal */}
      {isPreviewOpen && selectedFile && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-[#1E3D1A]">
                {selectedFile.title}
              </h3>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="h-[calc(90vh-4rem)]">
              <iframe
                src={`${selectedFile.url}#toolbar=0`}
                className="w-full h-full"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Regulation;
